import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, UserPlus, LogIn, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const inp = {
  width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8,
  fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box', color: '#111827',
  backgroundColor: 'white', transition: 'border-color 0.15s',
};
const labelS = { display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: 5 };
const btnPrimary = {
  width: '100%', padding: '11px', borderRadius: 8, backgroundColor: '#111827', color: 'white',
  border: 'none', fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center', gap: 8,
};
const btnOutline = {
  width: '100%', padding: '11px', borderRadius: 8, backgroundColor: 'white', color: '#111827',
  border: '1px solid #D1D5DB', fontWeight: 600, fontSize: '0.9375rem', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/qso';
  const { login } = useAuth();
  const [mode, setMode] = useState('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ prenom: '', nom: '', indicatif: '', email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const response = await authAPI.login({ email, password, twoFactorToken: twoFactorToken || undefined });
      
      // Si le 2FA est requis
      if (response.data.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setLoading(false);
        return;
      }
      
      login(response.data.token, response.data.user);
      navigate(from, { replace: true });
    } catch (err) { 
      setError(err.response?.data?.error || 'Erreur lors de la connexion'); 
      setTwoFactorToken(''); // Reset le token en cas d'erreur
    }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await authAPI.register(formData); setSuccess(true); }
    catch (err) { setError(err.response?.data?.error || "Erreur lors de l'inscription"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{display:'flex',justifyContent:'center',padding:'32px 20px 60px'}}>
      <div style={{width:'100%',maxWidth:440}}>

        {/* En-tête */}
        <div style={{marginBottom:28,textAlign:'center'}}>
          {mode === 'choose' && <>
            <h1 style={{fontSize:'1.625rem',fontWeight:800,color:'#111827',margin:'0 0 6px'}}>Espace membres</h1>
            <p style={{color:'#6B7280',fontSize:'0.9375rem',margin:0}}>Connectez-vous ou rejoignez le club HB9G.</p>
          </>}
          {mode === 'login' && <>
            <h1 style={{fontSize:'1.625rem',fontWeight:800,color:'#111827',margin:'0 0 6px'}}>Connexion</h1>
            <p style={{color:'#6B7280',fontSize:'0.9375rem',margin:0}}>Content de vous revoir.</p>
          </>}
          {mode === 'register' && !success && <>
            <h1 style={{fontSize:'1.625rem',fontWeight:800,color:'#111827',margin:'0 0 6px'}}>Rejoindre le club</h1>
            <p style={{color:'#6B7280',fontSize:'0.9375rem',margin:0}}>Votre demande sera validée par le comité.</p>
          </>}
        </div>

        {/* Carte */}
        <div style={{backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:12,padding:'28px 24px',boxShadow:'0 2px 12px rgba(0,0,0,0.07)'}}>

          {/* ── Mode choose ── */}
          {mode === 'choose' && (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <button onClick={() => setMode('login')} style={btnPrimary}>
                <LogIn style={{width:18,height:18}}/> Me connecter
              </button>
              <div style={{display:'flex',alignItems:'center',gap:10,margin:'2px 0'}}>
                <div style={{flex:1,height:1,backgroundColor:'#E5E7EB'}}/>
                <span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>Nouveau membre ?</span>
                <div style={{flex:1,height:1,backgroundColor:'#E5E7EB'}}/>
              </div>
              <button onClick={() => setMode('register')} style={btnOutline}>
                <UserPlus style={{width:18,height:18}}/> Demander un accès
              </button>
            </div>
          )}

          {/* ── Mode login ── */}
          {mode === 'login' && !success && (
            <form onSubmit={handleLogin} style={{display:'flex',flexDirection:'column',gap:16}}>
              <button type="button" onClick={() => { setMode('choose'); setError(''); }} style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'none',cursor:'pointer',color:'#6B7280',fontSize:'0.8125rem',padding:0,marginBottom:4}}>
                <ArrowLeft style={{width:13,height:13}}/> Retour
              </button>
              {error && <div style={{backgroundColor:'#FEF2F2',border:'1px solid #FECACA',color:'#DC2626',padding:'10px 14px',borderRadius:8,fontSize:'0.875rem'}}>{error}</div>}
              <div>
                <label style={labelS}>Adresse email</label>
                <input type="email" required autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} style={inp} placeholder="votre.email@example.com"/>
              </div>
              <div>
                <label style={labelS}>Mot de passe</label>
                <input type="password" required autoComplete="current-password" value={password} onChange={e=>setPassword(e.target.value)} style={inp} placeholder="••••••••"/>
              </div>
              {requiresTwoFactor && (
                <div>
                  <label style={labelS}>Code d'authentification à deux facteurs</label>
                  <input 
                    type="text" 
                    required 
                    value={twoFactorToken} 
                    onChange={e=>setTwoFactorToken(e.target.value.replace(/\s/g,''))} 
                    style={{...inp,textAlign:'center',letterSpacing:'0.3em',fontFamily:'monospace',fontSize:'1.125rem'}} 
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                  />
                  <p style={{fontSize:'0.75rem',color:'#6B7280',marginTop:6,marginBottom:0}}>
                    Entrez le code à 6 chiffres de votre application d'authentification ou un code de secours.
                  </p>
                </div>
              )}
              <button type="submit" disabled={loading} style={{...btnPrimary,marginTop:4,opacity:loading?0.7:1}}>
                {loading ? <div style={{width:18,height:18,border:'2px solid white',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/> : <><span>Se connecter</span><ArrowRight style={{width:16,height:16}}/></>}
              </button>
              <div style={{textAlign:'center',marginTop:4}}>
                <Link to="/reset-password" style={{fontSize:'0.8125rem',color:'#2563EB',textDecoration:'none',fontWeight:500}}>
                  Mot de passe oublié ?
                </Link>
              </div>
            </form>
          )}

          {/* ── Mode register ── */}
          {mode === 'register' && !success && (
            <form onSubmit={handleRegister} style={{display:'flex',flexDirection:'column',gap:14}}>
              <button type="button" onClick={() => { setMode('choose'); setError(''); }} style={{display:'flex',alignItems:'center',gap:5,background:'none',border:'none',cursor:'pointer',color:'#6B7280',fontSize:'0.8125rem',padding:0,marginBottom:4}}>
                <ArrowLeft style={{width:13,height:13}}/> Retour
              </button>
              {error && <div style={{backgroundColor:'#FEF2F2',border:'1px solid #FECACA',color:'#DC2626',padding:'10px 14px',borderRadius:8,fontSize:'0.875rem'}}>{error}</div>}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <label style={labelS}>Prénom</label>
                  <input type="text" required value={formData.prenom} onChange={e=>setFormData({...formData,prenom:e.target.value})} style={inp}/>
                </div>
                <div>
                  <label style={labelS}>Nom</label>
                  <input type="text" required value={formData.nom} onChange={e=>setFormData({...formData,nom:e.target.value})} style={inp}/>
                </div>
              </div>
              <div>
                <label style={labelS}>Indicatif radio</label>
                <input type="text" required placeholder="ex: HB9XYZ" value={formData.indicatif} onChange={e=>setFormData({...formData,indicatif:e.target.value.toUpperCase()})} style={{...inp,fontFamily:'monospace',textTransform:'uppercase'}}/>
              </div>
              <div>
                <label style={labelS}>Email</label>
                <input type="email" required value={formData.email} onChange={e=>setFormData({...formData,email:e.target.value})} style={inp}/>
              </div>
              <div>
                <label style={labelS}>Mot de passe <span style={{fontWeight:400,color:'#9CA3AF'}}>(min. 8 caractères)</span></label>
                <input type="password" required minLength={8} value={formData.password} onChange={e=>setFormData({...formData,password:e.target.value})} style={inp} placeholder="••••••••"/>
              </div>
              <button type="submit" disabled={loading} style={{...btnPrimary,marginTop:4,opacity:loading?0.7:1}}>
                {loading ? <div style={{width:18,height:18,border:'2px solid white',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/> : 'Demander mon accès'}
              </button>
            </form>
          )}

          {/* ── Succès ── */}
          {success && (
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',gap:16,padding:'8px 0'}}>
              <div style={{width:56,height:56,backgroundColor:'#D1FAE5',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <CheckCircle2 style={{width:28,height:28,color:'#059669'}}/>
              </div>
              <div>
                <div style={{fontSize:'1.25rem',fontWeight:800,color:'#111827',marginBottom:6}}>Demande envoyée !</div>
                <p style={{color:'#6B7280',fontSize:'0.875rem',margin:0,lineHeight:1.6}}>
                  Un email a été envoyé à <strong style={{color:'#111827'}}>{formData.email}</strong>.<br/>
                  Le comité validera votre accès prochainement.
                </p>
              </div>
              <button onClick={() => navigate('/qso')} style={{...btnOutline,marginTop:4}}>Retour à l'accueil</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
